import React from "react";
import { CalendarClock, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Kicker } from "../components/Reveal";
import { ContactForm } from "../components/ContactForm";
import { CALENDLY_URL } from "../data/content";

const ServiceLanding = ({ pageId, kicker, headline, subheadline, sections, topic, serviceChip }) => (
  <div data-testid={pageId}>
    <section className="relative bg-[#0A2B3D] text-white overflow-hidden" data-testid={`${pageId}-hero`}>
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: "linear-gradient(#A9CFE2 1px, transparent 1px), linear-gradient(90deg, #A9CFE2 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.06 }}
        transition={{ duration: 1.4 }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 md:px-12 py-12 md:py-24 flex flex-col lg:grid lg:grid-cols-12 lg:gap-14 lg:items-center">
        <div className="order-1 lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <Kicker light>{kicker}</Kicker>
          </motion.div>
          <motion.h1
            className="mt-6 font-serif font-bold text-4xl sm:text-5xl lg:text-[3.4rem] tracking-tight leading-[1.08]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            {headline}
          </motion.h1>
          <motion.p
            className="mt-6 max-w-xl text-base md:text-lg text-white/75 leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            {subheadline}
          </motion.p>
        </div>
        <motion.div
          className="order-2 lg:col-span-5 mt-8 lg:mt-0"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          data-testid={`${pageId}-hero-form`}
        >
          <ContactForm compact defaultTopic={topic} defaultServices={[serviceChip]} />
        </motion.div>
        <motion.div
          className="order-3 lg:col-span-7 mt-7 lg:mt-8"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
        >
          <a href={CALENDLY_URL} data-testid={`${pageId}-cta-call`} className="btn-outline-light justify-center">
            <CalendarClock size={16} /> Prefer to talk? Schedule a call
          </a>
        </motion.div>
      </div>
    </section>

    <section className="py-20 md:py-28" data-testid={`${pageId}-sections`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>How We Help</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Focused support, delivered by practitioners.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sections.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.06}>
              <div className="h-full p-8 bg-[#EBF3F8] border border-[#A9CFE2]/50 hover:border-[#0A2B3D]/30 transition-colors duration-500" data-testid={`${pageId}-section-${i}`}>
                <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{s.title}</h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section id="lp-form" className="bg-[#A9CFE2] py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid={`${pageId}-form-section`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <Kicker>Get Started</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight leading-[1.15]">
            Start the conversation.
          </h2>
          <ul className="mt-8 space-y-3.5">
            {[
              "A member of our team will contact you to discuss your needs",
              "Practical, right-sized recommendations — no obligation",
              "Decades of seat-level experience, at your side",
            ].map((item) => (
              <li key={item} className="flex gap-3 text-sm text-[#0A2B3D]/85 leading-relaxed">
                <Check size={15} className="text-[#0A2B3D] mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-10 p-6 bg-[#0A2B3D] text-white" data-testid={`${pageId}-calendly-card`}>
            <p className="eyebrow text-[#A9CFE2] mb-3">Prefer to Talk?</p>
            <p className="font-serif font-bold text-xl">Schedule an introductory call</p>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">
              Skip the form and pick a time that works for you — we'll discuss your needs directly.
            </p>
            <a href={CALENDLY_URL} data-testid={`${pageId}-calendly-link`} className="btn-gold mt-5">
              <CalendarClock size={16} /> Book a Time
            </a>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <ContactForm defaultTopic={topic} defaultServices={[serviceChip]} />
        </Reveal>
      </div>
    </section>
  </div>
);

const TRUST_LP = {
  pageId: "lp-trust-services",
  kicker: "Trust Services",
  headline: "Trust Company Compliance & Fiduciary Support",
  subheadline: "Practical guidance for trust companies, RIAs and fiduciary businesses.",
  topic: "Trust Services",
  serviceChip: "Trust Services Consulting",
  sections: [
    { title: "Regulatory & Compliance Support", body: "Fiduciary compliance programs aligned to your chartering jurisdiction — account administration standards, conflicts management, prudent-investor practices, and ongoing regulatory monitoring." },
    { title: "Governance & Oversight", body: "Board and committee structures, fiduciary roles and responsibilities, meeting cadence, minutes and recordkeeping — the oversight disciplines examiners expect to see." },
    { title: "Policies, Procedures & Controls", body: "Right-sized fiduciary policies and procedures with the documentation and controls to make them defensible in any examination." },
    { title: "Exam Readiness", body: "Mock examinations, examiner-request readiness, remediation planning, and steady support through regulatory examination cycles." },
    { title: "Operational Support", body: "Operational design for trust accounting, account opening and review, distributions, fee administration, and vendor oversight — built for accuracy and scale." },
    { title: "Formation, Affiliation & Growth Support", body: "Feasibility and strategic assessment, charter readiness, business plans and pro formas that stand up to regulator scrutiny — whether forming, affiliating, or growing." },
  ],
};

const COMPLIANCE_LP = {
  pageId: "lp-compliance-consulting",
  kicker: "Compliance Consulting",
  headline: "Compliance Consulting for Financial Services Firms",
  subheadline: "Practical compliance support for RIAs, broker-dealers, private funds and fiduciary businesses.",
  topic: "Compliance Consulting",
  serviceChip: "Compliance Consulting",
  sections: [
    { title: "Compliance Program Reviews", body: "An independent assessment of your compliance program against SEC, FINRA, and state expectations — with prioritized, practical remediation." },
    { title: "Policies & Procedures", body: "Policies and procedures built for your business model — written to be followed, evidenced, and defended in an examination." },
    { title: "Annual Reviews & Mock Exams", body: "Rule 206(4)-7 annual reviews and mock examinations that surface issues before regulators do." },
    { title: "Regulatory Readiness", body: "Monitoring of regulatory releases, filing calendars, and examination preparation that keeps your program current." },
    { title: "AML Compliance Support", body: "AML program design, independent testing under FINRA Rule 3310, and gap remediation for broker-dealers and online platforms." },
    { title: "Outsourced Compliance Projects", body: "Targeted project support — from marketing-rule reviews to electronic communications surveillance — without adding headcount." },
    { title: "Operational & Governance Support", body: "Governance frameworks, supervisory procedures, and the recurring documentation that keeps a compliance program effective." },
  ],
};

const AML_LP = {
  pageId: "lp-aml-independent-audits",
  kicker: "AML Independent Audits",
  headline: "Independent AML Testing for Broker-Dealers",
  subheadline:
    "FINRA Rule 3310 requires member firms to have their AML program tested independently — generally each year. American Elm Advisors is an experienced outside provider for broker-dealers approaching their testing cycle, including firms running online platforms and digital onboarding.",
  topic: "AML Independent Audit",
  serviceChip: "AML Independent Audit",
  sections: [
    { title: "Independent AML Testing", body: "An independent review of your AML program and how it operates in practice — policies and procedures, risk assessment, CIP/CDD, monitoring, escalation, and testing evidence — scoped to your business and risk profile." },
    { title: "Built for Broker-Dealers", body: "Testing shaped by your firm's actual activities — customers, products, supervisory structure, and the specific money-laundering risks your business presents — rather than a generic checklist." },
    { title: "Online Platforms & Digital Onboarding", body: "Particular experience with firms running online platforms and digital account opening, where remote onboarding, identity verification, and transaction monitoring raise distinct AML considerations." },
    { title: "Approaching Your Testing Cycle", body: "Practical help planning the scope and timing as your annual independent-testing date approaches, so the review fits your calendar and your documentation is organized in advance." },
    { title: "An Outside Option — Not a Mandate", body: "The rule calls for independent testing; it does not require every firm to outsource. A qualified, independent internal resource may suffice. Where an experienced outside tester is the right fit, we can help. No particular regulatory outcome is promised." },
    { title: "Findings & Remediation Support", body: "A documented report covering scope, methodology, observations, and recommendations — distinguishing urgent items from longer-term improvements — with optional help prioritizing remediation while final decisions remain with your firm." },
  ],
};

const EXPERT_LP = {
  pageId: "lp-expert-witness",
  kicker: "Expert Witness",
  headline: "Securities & Insurance Expert Witness for FINRA Arbitration",
  subheadline:
    "Independent expert analysis and testimony for counsel in FINRA Dispute Resolution arbitrations and related securities and insurance matters — supervision, suitability, fiduciary standards, annuities, and alternative investments. Decades of seat-level industry experience.",
  topic: "Expert Witness Services",
  serviceChip: "Expert Witness Services",
  sections: [
    { title: "FINRA Arbitration Experience", body: "Independent analysis and testimony in FINRA Dispute Resolution arbitrations for claimant- and respondent-side counsel — grounded in how broker-dealers and RIAs actually supervise, document, and operate." },
    { title: "Supervision & Suitability", body: "Opinions on supervisory systems, reasonable-basis and customer-specific suitability, Reg BI, and the standards firms are expected to meet — explained clearly for arbitrators and juries." },
    { title: "Fiduciary & Standard of Care", body: "Assessment of adviser conduct against fiduciary duties and applicable standards of care, including conflicts, disclosure, and the Investment Advisers Act framework." },
    { title: "Annuities & Insurance Products", body: "Deep experience with variable and fixed annuities, replacements, and insurance-product sales practices — a frequent focus of arbitration and regulatory disputes." },
    { title: "Alternative & Complex Products", body: "Analysis of non-traded REITs, private placements, structured products, and other complex or illiquid investments, and how they were marketed and supervised." },
    { title: "Reports, Depositions & Testimony", body: "Clear, defensible expert reports; deposition preparation and testimony; and credible, measured presentation at hearing. Conflict check and engagement terms come first." },
  ],
};

const CAPITAL_LP = {
  pageId: "lp-capital-markets",
  kicker: "Capital Markets",
  headline: "Capital Markets & Distribution Consulting for Private-Fund Sponsors",
  subheadline:
    "Practical help for sponsors raising under Rule 506(b) and 506(c) — distribution strategy, target-market messaging, product structuring, and due-diligence infrastructure to reach RIAs, broker-dealers, and other intermediaries. Strategy and diligence support, not a promise of capital or investor access.",
  topic: "Capital Markets Consulting",
  serviceChip: "Capital Markets Consulting",
  sections: [
    { title: "Distribution Strategy", body: "Define the target market, the investor message, and the channels that fit the offering — reaching RIAs, retail broker-dealers, and institutions with positioning that resonates." },
    { title: "Product Structuring", body: "Help management assess the commercial, economic, and operating implications of different structures (Reg D offerings, BDCs, interval funds and other vehicles) alongside the work of counsel." },
    { title: "Due-Diligence Infrastructure", body: "Build the diligence materials, data, and operating readiness intermediaries expect — so your offering stands up to scrutiny before it reaches investors." },
    { title: "Sponsor & Product Messaging", body: "Strategic, practical feedback on investor presentations, webinars, and communications within an agreed scope — clear message, required disclosures, and a defined review process." },
    { title: "Sales-Team Enablement", body: "Work with management to develop teams that communicate the offering and its benefits effectively, with feedback to ensure clarity in presenting the opportunity." },
    { title: "Coordination With Counsel & Providers", body: "Help align the commercial, distribution, diligence, and operating workstreams with your legal counsel, administrators, and any registered intermediary — without replacing their roles." },
  ],
};

export const TrustServicesLanding = () => <ServiceLanding {...TRUST_LP} />;
export const ComplianceLanding = () => <ServiceLanding {...COMPLIANCE_LP} />;
export const AmlAuditsLanding = () => <ServiceLanding {...AML_LP} />;
export const ExpertWitnessLanding = () => <ServiceLanding {...EXPERT_LP} />;
export const CapitalMarketsLanding = () => <ServiceLanding {...CAPITAL_LP} />;
